<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import HelpIconWithModal from '@/corePagesModule/components/HelpIconWithModal.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import { ReadFileService } from '@/registerInputModule/service/ReadFileService';
import { ref, watch } from 'vue';

const t = i18n.t;

const props = defineProps<{
  store: RegisterSettingStore;
}>();

const { state } = storeToRefs(props.store);

const emits = defineEmits<{
  (e: 'on-click-next'): void;
  (e: 'on-click-back'): void;
}>();

const imagesRequire = require.context('@/assets/img', false, /^\.\/register-input-step-flow-1.*\.png$/);
const localedRegisterInputStepFlowImage = ref(imagesRequire('./register-input-step-flow-1.png'));

watch(
  () => i18n.locale,
  (newVal) => {
    localedRegisterInputStepFlowImage.value =
      newVal === 'en' ? imagesRequire('./register-input-step-flow-1__en.png') : imagesRequire('./register-input-step-flow-1.png');
  },
  {
    immediate: true,
  }
);

/**
 * 戻るボタン
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

/**
 * 次へボタン
 */
const onClickNextButton = () => {
  emits('on-click-next');
};
</script>

<template>
  <div class="upload-business">
    <div class="upload-business__title">{{ t('取引アップロード：タイトル') }}</div>

    <img :src="localedRegisterInputStepFlowImage" class="upload-business__progress" />

    <div class="upload-business__sub-title sub-title">
      <div class="sub-title__text">
        <span class="sub-title__text--note"> {{ t('取引アップロード：本文') }}</span>
        <!-- STOPSHIP: ヘルプアイコンをfigma参考に挿入する -->
        <HelpIconWithModal :use-transform="true" class="sub-title__text--help-icon">
          {{ t('') }}
        </HelpIconWithModal>
      </div>
      <div class="sub-title__header-input header-input">
        <span class="header-input__label">{{ t('行番号') }}</span>
        <div class="header-input__input-text">
          <input
            v-model="state.uploadBusiness.businessInputHeaderNumber.value"
            class="header-input__input-text--form"
            type="number"
            :placeholder="t('半角数字')"
          />
          <span class="header-input__input-text--unit">{{ t('行') }}</span>
        </div>
        <span v-if="state.uploadBusiness.businessInputHeaderNumber.validateError == 'AtLeastOne'" class="header-input__input-text--error">{{
          t('取引設定：数値バリデーション：1以上エラー')
        }}</span>
        <span v-else-if="state.uploadBusiness.businessInputHeaderNumber.validateError == 'Empty'" class="header-input__input-text--error">{{
          t('取引設定：数値バリデーション：空文字エラー')
        }}</span>
        <span v-else class="header-input__input-text--error">{{ '　' }}</span>
      </div>
    </div>

    <div class="upload-business__upload-container">
      <div class="mb-20px dropzone" v-bind="store.getRootProps()">
        <input :data-testid="`dropzone_input_area`" v-bind="store.getInputProps()" />
        <div v-if="state.uploadBusiness.inputFile != null" :data-testid="`input_file_content`" class="dz-file-preview dz-preview">
          <div v-for="(file, index) in [state.uploadBusiness.inputFile]" :key="index" class="dz-file-content">
            <div class="dz-details">
              <div class="file-name-and-size">
                <span>{{ ReadFileService.fetchNameForEvent(file) }}</span>
                <span>{{ ReadFileService.getFileSizeStr(file) }}</span>
              </div>
              <span class="dz-remove" @click.stop="store.handleClickDeleteFile(index)"> Delete </span>
            </div>
          </div>
        </div>
        <div class="dz-default">
          <div class="dz-message" :class="store.isDragActive">
            <!-- STOPSHIP: 入力されたファイルのUIが微妙なので修正する -->
            <i />
            <p>{{ t('ここにファイルをドロップ') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="upload-business__button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
      <TextButton
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickNextButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：次へボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.upload-business {
  display: grid;
  gap: 12px;

  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  margin-top: 16px;
  padding: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__progress {
    justify-self: center;
  }

  &__upload-container {
    margin-top: 16px;
  }

  &__button-container {
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%));
    margin-top: 16px;
  }
}

.sub-title {
  display: grid;
  gap: 4px;
  grid-template-rows: repeat(2, fit-content(100%));

  &__text {
    display: grid;
    gap: 4px;
    grid-template-columns: repeat(2, fit-content(100%));

    &--note {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      color: $textDefault;
    }

    &--help-icon {
      padding-top: 3px; // なんか表示がずれるので調整
      margin-left: 2px;
    }
  }

  &__header-input {
    margin-top: 16px;
  }
}

.header-input {
  display: grid;
  gap: 8px;
  grid-template-rows: repeat(3, fit-content(100%));

  &__label {
    font-family: Noto Sans JP;
    font-size: $fontSizeS;
    font-weight: 400;
    color: $textDefault;
  }

  &__input-text {
    &--form {
      border: none;
      outline: none;
      border-bottom: 1px solid $borderDefault;
      padding: 6px 8px;
    }

    &--error {
      font-family: Noto Sans JP;
      font-size: $fontSizeS;
      font-weight: 400;
      color: $semanticError;
    }
  }
}
</style>
